/**
 * **Le site vitrine en deux langues** (#2374).
 *
 * ⚠️ **Une adresse par langue, pas une bascule.** L'i18n du produit (`react-i18next`) est un hook
 * **client** : l'employer ici imposerait de passer chaque section en composant client, donc de perdre
 * le rendu serveur — exactement ce que #1290 exigeait pour la grille tarifaire, *« les prix doivent
 * être dans le HTML, pour les moteurs comme pour qui lit la page sans attendre un second
 * aller-retour »*. Et Google n'indexerait qu'une langue.
 *
 * Le français reste **à la racine** : les adresses existantes ne bougent pas, donc aucun lien
 * partagé, aucun signet et aucune position acquise ne se perdent. L'anglais est préfixé.
 */
export type LangueVitrine = 'fr' | 'en';

export const LANGUE_PAR_DEFAUT: LangueVitrine = 'fr';

/**
 * Les pages de la vitrine, et leur adresse dans chaque langue.
 *
 * ⚠️ **Les adresses anglaises sont traduites, pas préfixées telles quelles.** `/en/tarifs` serait une
 * adresse anglaise portant un mot français : elle se lit mal, et se référence encore plus mal.
 *
 * ⚠️ **Une page absente de cette table n'existe pas en anglais**, et c'est un état normal : le
 * sélecteur ne propose alors pas l'autre langue plutôt que de mener à une page qui n'existe pas.
 * C'est ce qui permet de traduire le site page par page sans jamais le laisser à moitié cassé.
 */
export const PAGES_TRADUITES: readonly { fr: string; en: string }[] = [
  { fr: '/', en: '/en' },
  { fr: '/fonctionnalites', en: '/en/features' },
  { fr: '/tarifs', en: '/en/pricing' },
  { fr: '/modules', en: '/en/modules' },
  { fr: '/le-metier', en: '/en/the-business' },
  { fr: '/avantages', en: '/en/benefits' },
  { fr: '/espace-web', en: '/en/web-app' },
  { fr: '/applications-mobiles', en: '/en/mobile-apps' },
  { fr: '/support', en: '/en/support' },
  { fr: '/demander-une-demo', en: '/en/request-a-demo' },
  /*
    ⚠️ **Les trois pages juridiques sont des traductions de confort.** Chacune porte, en tête, la
    mention que seule la version française fait foi, et un lien vers elle — la mention réduit le
    risque, elle ne l'annule pas.
  */
  { fr: '/mentions-legales', en: '/en/legal-notice' },
  { fr: '/confidentialite', en: '/en/privacy' },
  { fr: '/conditions-generales', en: '/en/terms' },
  /*
    ⚠️ **Cette table ne liste que ce qui est traduit.** Les autres pages y entreront à mesure ; tant
    qu'elles n'y sont pas, le sélecteur ne les propose pas en anglais et le bandeau ne les montre pas
    — plutôt que de mener à une adresse qui n'existe pas, ou de sortir de la langue sans le dire.

    Les adresses anglaises sont **traduites**, pas préfixées telles quelles : `/en/tarifs` serait une
    adresse anglaise portant un mot français.

    **Toutes les pages de la vitrine y figurent désormais.** La table reste le seul endroit qui
    décide : une page retirée d'ici disparaît du sélecteur et du bandeau anglais, plutôt que de mener
    à une adresse qui n'existe plus.
*/
];

/**
 * **L'équivalent d'une page dans l'autre langue**, ou `null` s'il n'existe pas.
 *
 * ⚠️ Le sélecteur doit mener à **la même page**, jamais à l'accueil : perdre sa page en changeant de
 * langue est le défaut le plus courant de ces sélecteurs, et le plus agaçant — on relit la page
 * qu'on venait de trouver.
 */
export function adresseDansLaLangue(chemin: string, cible: LangueVitrine): string | null {
  const page = PAGES_TRADUITES.find(p => p.fr === chemin || p.en === chemin);
  if (page) return page[cible];

  /*
    ⚠️ **Le français n'est pas une traduction optionnelle.** Toute page existe à son adresse
    française, qu'elle figure ou non dans la table — la table dit seulement lesquelles ont une
    version anglaise.

    Le confondre a coûté cher : filtrer le bandeau et le pied de page sur cette fonction leur avait
    fait perdre toutes les entrées non encore traduites, **en français**. Le site français s'était
    vidé de sa navigation pour une table qui ne parlait que de l'anglais.
  */
  return cible === 'fr' ? chemin : null;
}

/** La langue d'une adresse. Tout ce qui n'est pas préfixé est français, par construction. */
export const langueDeLAdresse = (chemin: string): LangueVitrine =>
  chemin === '/en' || chemin.startsWith('/en/') ? 'en' : 'fr';

/**
 * Les deux adresses d'une page, pour `hreflang`.
 *
 * Sans elles, Google traite les deux versions comme du **contenu dupliqué** et n'en retient qu'une —
 * ce qui annulerait l'essentiel du bénéfice de la traduction.
 */
export function alternatesDe(chemin: string): Record<string, string> | null {
  const page = PAGES_TRADUITES.find(p => p.fr === chemin || p.en === chemin);
  if (!page) return null;
  return { 'fr-FR': page.fr, en: page.en, 'x-default': page.fr };
}
